import { useState, useEffect, useCallback } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import TeamReportsSection from "../components/performance/TeamReportsSection";
import {
  Flame, Star, Trophy, Shield, Zap, Crown, Target, Award, Eye, Heart,
  Sparkles, Calendar, Gem, Swords, Medal, Crosshair, Activity,
  ChevronRight, Lock, CheckCircle, TrendingUp, Brain, UserCheck,
  FileText, MessageSquare, Users
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACHIEVEMENT_ICONS = {
  star: Star, shield: Shield, flame: Flame, zap: Zap, trophy: Trophy,
  crown: Crown, target: Target, medal: Medal, crosshair: Crosshair,
  award: Award, calendar: Calendar, gem: Gem, swords: Swords,
  eye: Eye, heart: Heart, sparkles: Sparkles,
};

const LEVEL_COLORS = {
  1: "from-gray-400 to-gray-500",
  2: "from-green-400 to-green-600",
  3: "from-blue-400 to-blue-600",
  4: "from-purple-400 to-purple-600",
  5: "from-amber-400 to-amber-600",
  6: "from-rose-400 to-rose-600",
  7: "from-cyan-400 to-cyan-600",
  8: "from-orange-400 to-orange-600",
  9: "from-pink-400 to-pink-600",
  10: "from-yellow-300 to-yellow-500",
};

export default function PerformanceTracker({ user, onLogout }) {
  const { user: authUser } = useAuth();
  const [gameProfile, setGameProfile] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [showAddMetric, setShowAddMetric] = useState(false);
  const [viewMode, setViewMode] = useState("personal");
  const [teamReports, setTeamReports] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("weekly");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ start: "", end: "" });
  const [userPermissions, setUserPermissions] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [teamTrends, setTeamTrends] = useState(null);
  const [showTrendCharts, setShowTrendCharts] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedComparisonPlayers, setSelectedComparisonPlayers] = useState([]);
  const [performanceAlerts, setPerformanceAlerts] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [formData, setFormData] = useState({
    mood: 5, stress_level: 5, focus_level: 5,
    confidence: 5, sleep_quality: 5, performance_rating: 5, notes: ""
  });

  const canViewTeamReports = authUser && (
    ["coach", "sport-performance-consultant", "team-admin", "school-admin", "system-admin"].includes(authUser.role) ||
    userPermissions.some(p => p.permission_type === "view_team_reports" && p.is_active)
  );

  const fetchGameProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/gamification/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGameProfile(res.data);
    } catch (e) {
      console.error("Failed to load game profile");
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/performance/metrics?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMetrics(res.data);
    } catch (e) {
      console.error("Failed to load metrics");
    }
  }, []);

  useEffect(() => {
    fetchGameProfile();
    fetchMetrics();
  }, [fetchGameProfile, fetchMetrics]);

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/performance/metrics`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      toast.success("Metrics saved! +10 XP");
      setShowAddMetric(false);
      setFormData({ mood: 5, stress_level: 5, focus_level: 5, confidence: 5, sleep_quality: 5, performance_rating: 5, notes: "" });
      fetchMetrics();
      fetchGameProfile();
    } catch (e) {
      toast.error("Failed to save metric");
    }
  };

  const exportToCSV = () => {};
  const printReport = () => {};

  const metricFields = [
    { key: "mood", label: "Mood", color: "text-yellow-600" },
    { key: "stress_level", label: "Stress", color: "text-red-600" },
    { key: "focus_level", label: "Focus", color: "text-blue-600" },
    { key: "confidence", label: "Confidence", color: "text-green-600" },
    { key: "sleep_quality", label: "Sleep", color: "text-purple-600" },
    { key: "performance_rating", label: "Performance", color: "text-orange-600" },
  ];

  const gp = gameProfile;
  const levelGradient = gp ? (LEVEL_COLORS[gp.level.level] || LEVEL_COLORS[1]) : LEVEL_COLORS[1];

  // Radar chart data from latest metrics
  const radarData = metrics.length > 0
    ? metricFields.map(f => ({
        stat: f.label,
        value: metrics[0]?.[f.key] || 0,
        fullMark: 10,
      }))
    : metricFields.map(f => ({ stat: f.label, value: 0, fullMark: 10 }));

  return (
    <div className="min-h-screen bg-[#0f1623]">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-8">
          <div>
            <h1 data-testid="performance-title" className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-1">
              Performance HQ
            </h1>
            <p className="text-xs sm:text-sm text-gray-400">Level up your mental game</p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="add-metric-btn"
              onClick={() => setShowAddMetric(true)}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-emerald-500/20 text-xs sm:text-sm h-10"
            >
              <Sparkles className="w-4 h-4 mr-1 sm:mr-2" /> Check-In
            </Button>
            {canViewTeamReports && (
              <Button
                onClick={() => setViewMode(viewMode === "personal" ? "team" : "personal")}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                {viewMode === "personal" ? "Team" : "Personal"}
              </Button>
            )}
          </div>
        </div>

        {viewMode === "personal" ? (
          <>
            {gp && (
              <>
                {/* === TOP STATS: Streak + Weekly Averages + Consistency === */}
                <div className="rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-6 mb-6" data-testid="top-stats-box">
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Day Streak */}
                    <div data-testid="streak-card" className="flex flex-col items-center text-center">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-2 ${
                        gp.streak.current >= 7 ? 'bg-orange-500/20' :
                        gp.streak.current >= 3 ? 'bg-amber-500/20' : 'bg-gray-700/50'
                      }`}>
                        <Flame className={`w-7 h-7 ${
                          gp.streak.current >= 7 ? 'text-orange-400' :
                          gp.streak.current >= 3 ? 'text-amber-400' : 'text-gray-500'
                        }`} />
                      </div>
                      <p className="text-3xl font-black text-white">{gp.streak.current}</p>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Day Streak</p>
                      <p className="text-[10px] text-gray-600 mt-1">Best: {gp.streak.max} days</p>
                      {gp.streak.checked_in_today ? (
                        <span className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Today done
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setShowAddMetric(true)}
                          className="mt-2 text-xs bg-amber-500 hover:bg-amber-600 text-black font-bold h-7 px-3"
                        >
                          Check In Now
                        </Button>
                      )}
                    </div>

                    {/* Weekly Averages */}
                    <div data-testid="weekly-averages" className="lg:col-span-1">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 text-center">Weekly Averages</h3>
                      {metrics.length > 0 ? (
                        <div className="grid grid-cols-3 gap-2">
                          {metricFields.map((field) => {
                            const recent7 = metrics.slice(0, 7);
                            const prev7 = metrics.slice(7, 14);
                            const recentAvg = recent7.length > 0
                              ? (recent7.reduce((sum, m) => sum + (m[field.key] || 0), 0) / recent7.length).toFixed(1)
                              : "—";
                            const prevAvg = prev7.length > 0
                              ? prev7.reduce((sum, m) => sum + (m[field.key] || 0), 0) / prev7.length
                              : null;
                            const trend = prevAvg !== null && recentAvg !== "—"
                              ? parseFloat(recentAvg) - prevAvg
                              : null;
                            const isStress = field.key === "stress_level";
                            const trendPositive = trend !== null && (isStress ? trend < -0.3 : trend > 0.3);
                            const trendNegative = trend !== null && (isStress ? trend > 0.3 : trend < -0.3);
                            return (
                              <div key={field.key} className="text-center p-2 rounded-lg bg-gray-800/40 border border-gray-700/30">
                                <p className="text-[9px] text-gray-500 uppercase tracking-wider font-bold mb-0.5">{field.label}</p>
                                <p className={`text-lg font-black ${field.color}`}>{recentAvg}</p>
                                {trend !== null && (
                                  <div className={`flex items-center justify-center gap-0.5 text-[9px] font-bold ${
                                    trendPositive ? 'text-emerald-400' : trendNegative ? 'text-rose-400' : 'text-gray-500'
                                  }`}>
                                    {trendPositive ? <TrendingUp className="w-2.5 h-2.5" /> : trendNegative ? <TrendingUp className="w-2.5 h-2.5 rotate-180" /> : null}
                                    {Math.abs(trend).toFixed(1)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center text-gray-600 text-sm py-4">Complete check-ins to see averages</div>
                      )}
                    </div>

                    {/* Consistency Score */}
                    <div data-testid="consistency-score" className="flex flex-col items-center justify-center">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Consistency</h3>
                      {(() => {
                        const now = new Date();
                        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        const recentCheckins = metrics.filter(m => new Date(m.created_at || m.date) > weekAgo).length;
                        const consistencyPct = Math.min(100, Math.round((recentCheckins / 7) * 100));
                        const grade = consistencyPct >= 85 ? 'A' : consistencyPct >= 70 ? 'B' : consistencyPct >= 50 ? 'C' : consistencyPct >= 30 ? 'D' : 'F';
                        const gradeColor = consistencyPct >= 85 ? 'text-emerald-400' : consistencyPct >= 70 ? 'text-blue-400' : consistencyPct >= 50 ? 'text-amber-400' : 'text-rose-400';
                        return (
                          <div className="flex items-center gap-4">
                            <div className="relative w-20 h-20 flex-shrink-0">
                              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" stroke="#1e293b" strokeWidth="8" fill="none" />
                                <circle cx="50" cy="50" r="42" stroke="url(#consistencyGrad)" strokeWidth="8" fill="none"
                                  strokeDasharray={`${consistencyPct * 2.64} ${264 - consistencyPct * 2.64}`}
                                  strokeLinecap="round"
                                />
                                <defs>
                                  <linearGradient id="consistencyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#34d399" />
                                    <stop offset="100%" stopColor="#2dd4bf" />
                                  </linearGradient>
                                </defs>
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-2xl font-black ${gradeColor}`}>{grade}</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-2xl font-black text-white">{consistencyPct}%</p>
                              <p className="text-[10px] text-gray-500">{recentCheckins}/7 days</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* === LEVEL ROW === */}
                <div className="mb-6">
                  {/* Level Card */}
                  <div data-testid="level-card" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-6">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br opacity-10 rounded-full -translate-y-1/2 translate-x-1/2" style={{background: `linear-gradient(135deg, var(--tw-gradient-stops))`}} />
                    <div className="flex items-center gap-5">
                      {/* Level badge */}
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${levelGradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                        <span className="text-3xl font-black text-white">{gp.level.level}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Level {gp.level.level}</span>
                          <span className={`text-sm font-bold bg-gradient-to-r ${levelGradient} bg-clip-text text-transparent`}>
                            {gp.level.title}
                          </span>
                        </div>
                        <p className="text-2xl font-black text-white mb-2">
                          {gp.level.total_xp} <span className="text-sm font-medium text-gray-500">XP</span>
                        </p>
                        {/* XP Progress Bar */}
                        <div className="relative">
                          <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${levelGradient} transition-all duration-1000 ease-out`}
                              style={{ width: `${gp.level.progress_pct}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-gray-500">{gp.level.xp_in_level} / {gp.level.xp_for_next_level} XP</span>
                            {gp.level.next_level && (
                              <span className="text-[10px] text-gray-500">Next: {gp.level.next_level.title}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* === STATS RADAR + DAILY MISSIONS === */}
                <div className="grid lg:grid-cols-3 gap-4 mb-6">
                  {/* Radar Chart */}
                  <div data-testid="stats-radar" className="lg:col-span-1 rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-5">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Player Stats</h3>
                    {metrics.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#2a3454" />
                          <PolarAngleAxis dataKey="stat" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                          <Radar dataKey="value" stroke="#34d399" fill="#34d399" fillOpacity={0.15} strokeWidth={2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-gray-600 text-sm">
                        Complete a check-in to see your stats
                      </div>
                    )}
                  </div>

                  {/* Quest Board */}
                  <div data-testid="quest-board" className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-5">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Active Quests</h3>

                    {/* Daily missions */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                        gp.daily_missions.checkin ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-gray-800/50 border-gray-700/50'
                      }`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${gp.daily_missions.checkin ? 'bg-emerald-500' : 'bg-gray-700'}`}>
                          {gp.daily_missions.checkin ? <CheckCircle className="w-4 h-4 text-white" /> : <Activity className="w-4 h-4 text-gray-400" />}
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${gp.daily_missions.checkin ? 'text-emerald-400' : 'text-gray-300'}`}>Daily Check-In</p>
                          <p className="text-[10px] text-gray-500">+10 XP</p>
                        </div>
                      </div>
                      <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                        gp.daily_missions.training_session ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-gray-800/50 border-gray-700/50'
                      }`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${gp.daily_missions.training_session ? 'bg-emerald-500' : 'bg-gray-700'}`}>
                          {gp.daily_missions.training_session ? <CheckCircle className="w-4 h-4 text-white" /> : <Target className="w-4 h-4 text-gray-400" />}
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${gp.daily_missions.training_session ? 'text-emerald-400' : 'text-gray-300'}`}>Training Session</p>
                          <p className="text-[10px] text-gray-500">+15 XP</p>
                        </div>
                      </div>
                    </div>

                    {/* Training Plan Quests */}
                    {gp.quests.length > 0 ? (
                      <div className="space-y-3">
                        {gp.quests.map((quest) => {
                          const pct = quest.adherence_pct;
                          const barColor = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500";
                          return (
                            <div key={quest.plan_id} data-testid={`quest-${quest.plan_id}`} className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-purple-400" />
                                  <p className="text-sm font-semibold text-white truncate">{quest.title}</p>
                                </div>
                                <span className="text-xs font-bold text-gray-400">{quest.duration_weeks}w</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex-1">
                                  <div className="w-full h-2.5 bg-gray-700 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                                <span className="text-xs font-bold text-white">{pct}%</span>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[10px] text-gray-500">{quest.completed_days} / {quest.total_days} days</span>
                                <span className="text-[10px] text-gray-500">+15 XP per day</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Target className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No active training plans</p>
                        <a href="/plans" className="text-xs text-emerald-400 hover:text-emerald-300 mt-1 inline-block">
                          Create a plan to start earning XP
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* === ACHIEVEMENTS === */}
                <div data-testid="achievements-section" className="rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-6 mb-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                      Achievements ({gp.achievements.unlocked_count}/{gp.achievements.total})
                    </h3>
                    <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full"
                        style={{ width: `${(gp.achievements.unlocked_count / gp.achievements.total) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Unlocked */}
                  {gp.achievements.unlocked.length > 0 && (
                    <div className="mb-5">
                      <p className="text-xs text-emerald-400 font-semibold mb-3">Unlocked</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {gp.achievements.unlocked.map((ach) => {
                          const Icon = ACHIEVEMENT_ICONS[ach.icon] || Star;
                          return (
                            <div key={ach.id} data-testid={`achievement-${ach.id}`} className="group relative flex flex-col items-center text-center p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-yellow-600/5 border border-amber-500/20 hover:border-amber-400/40 transition-all">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-2 shadow-lg shadow-amber-500/20">
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <p className="text-[11px] font-bold text-white leading-tight">{ach.title}</p>
                              <p className="text-[9px] text-gray-500 mt-0.5 leading-tight">{ach.description}</p>
                              <span className="text-[9px] text-amber-400 font-bold mt-1">+{ach.xp} XP</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Locked */}
                  {gp.achievements.locked.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 font-semibold mb-3">Locked</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {gp.achievements.locked.map((ach) => {
                          const Icon = ACHIEVEMENT_ICONS[ach.icon] || Star;
                          return (
                            <div key={ach.id} data-testid={`achievement-locked-${ach.id}`} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-800/30 border border-gray-700/30 opacity-50">
                              <div className="w-10 h-10 rounded-xl bg-gray-700 flex items-center justify-center mb-2">
                                <Lock className="w-4 h-4 text-gray-500" />
                              </div>
                              <p className="text-[11px] font-bold text-gray-400 leading-tight">{ach.title}</p>
                              <p className="text-[9px] text-gray-600 mt-0.5 leading-tight">{ach.description}</p>
                              <span className="text-[9px] text-gray-600 font-bold mt-1">+{ach.xp} XP</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* === QUICK STATS === */}
                <div data-testid="quick-stats" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: "Check-Ins", value: gp.stats.checkins, icon: Activity, color: "text-emerald-400" },
                    { label: "Training Days", value: gp.stats.training_days, icon: Calendar, color: "text-blue-400" },
                    { label: "Goals Done", value: gp.stats.goals_completed, icon: Target, color: "text-amber-400" },
                    { label: "Plans", value: gp.stats.plans, icon: Shield, color: "text-purple-400" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl bg-[#161d2f] border border-white/5 p-4 flex items-center gap-3">
                      <s.icon className={`w-5 h-5 ${s.color}`} />
                      <div>
                        <p className="text-xl font-black text-white">{s.value}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{s.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* === PERSONAL BESTS === */}
                <div data-testid="personal-bests" className="rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-6 mb-6">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Personal Bests</h3>
                  {metrics.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {metricFields.filter(f => f.key !== 'stress_level').map((field) => {
                        const best = Math.max(...metrics.map(m => m[field.key] || 0));
                        const latest = metrics[0]?.[field.key] || 0;
                        const isAtBest = latest >= best;
                        return (
                          <div key={field.key} className="flex items-center gap-2 p-2 rounded-lg bg-gray-800/40">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isAtBest ? 'bg-amber-500/20' : 'bg-gray-700/50'}`}>
                              {isAtBest ? <Crown className="w-4 h-4 text-amber-400" /> : <Star className="w-4 h-4 text-gray-500" />}
                            </div>
                            <div>
                              <p className={`text-lg font-black ${isAtBest ? 'text-amber-400' : 'text-white'}`}>{best}</p>
                              <p className="text-[9px] text-gray-500 uppercase tracking-wider">{field.label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-20 text-gray-600 text-sm">
                      Complete check-ins to track personal bests
                    </div>
                  )}
                </div>

                {/* === RECENT CHECK-IN HISTORY === */}
                {metrics.length > 0 && (
                  <div data-testid="recent-checkins" className="rounded-2xl bg-gradient-to-br from-[#161d2f] to-[#1a2238] border border-white/5 p-6 mb-6">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Recent Check-Ins</h3>
                    <div className="space-y-2">
                      {metrics.slice(0, 7).map((m, i) => {
                        const date = new Date(m.created_at || m.date);
                        const avg = ((m.mood + m.focus_level + m.confidence + m.sleep_quality + m.performance_rating) / 5).toFixed(1);
                        const avgNum = parseFloat(avg);
                        const barColor = avgNum >= 7.5 ? 'bg-emerald-500' : avgNum >= 5 ? 'bg-amber-500' : 'bg-rose-500';
                        return (
                          <div key={m.id || i} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-800/30 hover:bg-gray-800/50 transition-colors">
                            <div className="text-center w-12 flex-shrink-0">
                              <p className="text-[10px] text-gray-500 uppercase">
                                {date.toLocaleDateString('en-US', { weekday: 'short' })}
                              </p>
                              <p className="text-xs font-bold text-gray-300">
                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                            <div className="flex-1">
                              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${avgNum * 10}%` }} />
                              </div>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              <div className="flex items-center gap-1.5">
                                {metricFields.map(f => (
                                  <span key={f.key} className={`text-[9px] font-bold ${f.color} bg-gray-800 px-1.5 py-0.5 rounded`}>
                                    {m[f.key]}
                                  </span>
                                ))}
                              </div>
                              <span className={`text-sm font-black ${avgNum >= 7.5 ? 'text-emerald-400' : avgNum >= 5 ? 'text-amber-400' : 'text-rose-400'}`}>
                                {avg}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {metrics.length > 7 && (
                      <p className="text-[10px] text-gray-600 text-center mt-3">Showing last 7 check-ins of {metrics.length} total</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Loading state */}
            {!gp && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-2xl bg-gray-800 flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Sparkles className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-500">Loading your profile...</p>
              </div>
            )}
          </>
        ) : (
          <TeamReportsSection
            performanceAlerts={performanceAlerts} showAlerts={showAlerts} setShowAlerts={setShowAlerts}
            filterOptions={filterOptions} selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
            selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam} teams={teams}
            selectedPlayers={selectedPlayers} setSelectedPlayers={setSelectedPlayers}
            selectedSport={selectedSport} setSelectedSport={setSelectedSport}
            selectedSchool={selectedSchool} setSelectedSchool={setSelectedSchool}
            selectedGender={selectedGender} setSelectedGender={setSelectedGender}
            selectedLevel={selectedLevel} setSelectedLevel={setSelectedLevel}
            customDateRange={customDateRange} setCustomDateRange={setCustomDateRange}
            showTrendCharts={showTrendCharts} setShowTrendCharts={setShowTrendCharts}
            teamTrends={teamTrends} comparisonMode={comparisonMode} setComparisonMode={setComparisonMode}
            selectedComparisonPlayers={selectedComparisonPlayers} setSelectedComparisonPlayers={setSelectedComparisonPlayers}
            teamReports={teamReports} exportToCSV={exportToCSV} printReport={printReport}
          />
        )}
      </div>

      {/* Add Metric Dialog */}
      <Dialog open={showAddMetric} onOpenChange={setShowAddMetric}>
        <DialogContent data-testid="add-metric-dialog" className="max-w-lg max-h-[80vh] overflow-y-auto bg-[#1a2238] border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" /> Daily Check-In
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Rate each aspect 1-10 to earn <span className="text-emerald-400 font-bold">+10 XP</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-3">
            {metricFields.map((field) => (
              <div key={field.key}>
                <div className="flex justify-between mb-2">
                  <Label className="text-gray-300 text-sm">{field.label}</Label>
                  <span className={`font-black text-lg ${field.color}`}>{formData[field.key]}</span>
                </div>
                <Slider
                  data-testid={`${field.key}-slider`}
                  min={1} max={10} step={1}
                  value={[formData[field.key]]}
                  onValueChange={(v) => setFormData({ ...formData, [field.key]: v[0] })}
                  className="w-full"
                />
              </div>
            ))}
            <div>
              <Label className="text-gray-300 text-sm">Notes (optional)</Label>
              <Textarea
                data-testid="metric-notes-textarea"
                placeholder="Any observations?"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-600"
              />
            </div>
            <Button
              data-testid="submit-metric-btn"
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 font-bold shadow-lg shadow-emerald-500/20"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Complete Check-In (+10 XP)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
